import { BarChart3, Bell, CalendarDays, CheckCircle2, ClipboardList, Clock, FileText, MessageCircle, Send, TrendingUp, Users } from "lucide-react";
import { StatusPill } from "@/components/StatusPill";
import { InitialsAvatar, VoiceBubble } from "./landingPrimitives";

/* ============================================================
 * §4 · KEEP JOBS MOVING — warm office environment
 * ============================================================ */
/* ============================================================
 * §4 · KEEP JOBS MOVING — full-bleed environmental canvas
 *   (Section-3-approved architecture)
 *   - Single environmental canvas at z-0 (desktop only)
 *   - Left zone (pale-blue negative-space panel): headline +
 *     body + 5 capability cards
 *   - Right zone (over wall-sign/bookshelf area, clear of Levi
 *     and the Today's Plan notebook): 5 live UI cards stacked
 *     vertically inside the grid column (NOT position:absolute)
 *   - Bottom: 5-item benefit strip (desktop only), constrained
 *     inside a centered max-w-7xl wrapper
 *   - Mobile renders its own vertical stack below the desktop
 *     content: Levi visual + right-side UI cards + benefit stack
 *   - No section header, no scrims/overlays, single instance
 *     of every element
 * ============================================================ */
export const KeepSection = ({ c }) => {
  const capabilities = [
    { icon: CalendarDays, title: c.keep.c1_title, body: c.keep.c1_body,
      testid: "keep-card-calendar",
      right: (
        <div className="rounded-md bg-white border border-[var(--jc-border)] px-2.5 py-1.5 text-right">
          <div className="text-[11px] uppercase tracking-wider text-[var(--jc-text-soft)] font-semibold">{c.keep.c1_when}</div>
          <div className="text-[14px] font-semibold text-[var(--jc-text)] leading-tight">{c.keep.c1_time}</div>
          <div className="text-[10.5px] text-[var(--jc-text-soft)]">{c.keep.c1_meta}</div>
        </div>
      )},
    { icon: Send, title: c.keep.c2_title, body: c.keep.c2_body,
      testid: "keep-card-updates",
      right: <StatusPill status="sent" kind="estimate" t={(k) => (k === "status.sent" ? c.keep.c2_status : k)} /> },
    { icon: FileText, title: c.keep.c3_title, body: c.keep.c3_body,
      testid: "keep-card-workorders",
      right: (
        <div className="flex items-center gap-1.5">
          <div className="text-right leading-tight">
            <div className="text-[11.5px] font-semibold text-[var(--jc-text)]">{c.keep.c3_label}</div>
            <div className="text-[11px] text-[var(--jc-text-soft)]">{c.keep.c3_label2}</div>
          </div>
          <span className="px-1.5 py-0.5 rounded bg-[var(--jc-red)] text-white text-[9px] font-bold">PDF</span>
        </div>
      )},
    { icon: Users, title: c.keep.c4_title, body: c.keep.c4_body,
      testid: "keep-card-crew",
      right: (
        <div className="flex items-center -space-x-1.5">
          <InitialsAvatar initials="TR" size={26} variant="navy" ring />
          <InitialsAvatar initials="LK" size={26} variant="blue" ring />
          <InitialsAvatar initials="AH" size={26} variant="navy" ring />
          <span className="ml-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-md bg-[#EEF3FE] text-[var(--jc-text)]">+3</span>
        </div>
      )},
    { icon: BarChart3, title: c.keep.c5_title, body: c.keep.c5_body,
      testid: "keep-card-activity",
      right: (
        <div className="w-28">
          <div className="flex items-center justify-between text-[10.5px] mb-1">
            <span className="text-[var(--jc-text-soft)] uppercase tracking-wider font-semibold">{c.keep.c5_progress}</span>
            <span className="font-semibold text-[var(--jc-text)]">76%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#EEF3FE] overflow-hidden">
            <div className="h-full bg-[var(--jc-blue)]" style={{ width: "76%" }} />
          </div>
          <div className="text-[10.5px] text-[var(--jc-text-soft)] mt-1">{c.keep.c5_next}</div>
        </div>
      )},
  ];
  const value = [
    { icon: Clock,         t: c.keep.value[0].t, b: c.keep.value[0].b, testid: "keep-value-1" },
    { icon: MessageCircle, t: c.keep.value[1].t, b: c.keep.value[1].b, testid: "keep-value-2" },
    { icon: ClipboardList, t: c.keep.value[2].t, b: c.keep.value[2].b, testid: "keep-value-3" },
    { icon: Bell,          t: c.keep.value[3].t, b: c.keep.value[3].b, testid: "keep-value-4" },
    { icon: TrendingUp,    t: c.keep.value[4].t, b: c.keep.value[4].b, testid: "keep-value-5" },
  ];
  const evtColors = [
    "bg-[#DBEAFE] text-[#1E40AF]", "bg-[#DCFCE7] text-[#14532D]", "bg-[#FEF3C7] text-[#92400E]",
    "bg-[#FEE2E2] text-[#991B1B]", "bg-[#E0E7FF] text-[#3730A3]", null, "bg-[#CCFBF1] text-[#115E59]",
  ];
  const evtLabels = [c.keep.cal_evts[0], c.keep.cal_evts[1], c.keep.cal_evts[2], c.keep.cal_evts[3], c.keep.cal_evts[4], null, c.keep.cal_evts[5]];

  // Right-side live UI stack — reused by desktop right column
  // and mobile-only bottom stack.
  const RightUI = ({ compact }) => (
    <div className={compact ? "space-y-3" : "space-y-3"} data-testid="keep-right-stack">
      <VoiceBubble testid="keep-martha-voice">{c.keep.martha_voice}</VoiceBubble>
      <div className="rounded-xl bg-white border border-white shadow-lg p-4" data-testid="keep-martha-sms">
        <div className="flex items-center gap-2.5">
          <InitialsAvatar initials="MJ" size={34} />
          <div className="flex-1 min-w-0">
            <div className="text-[14.5px] lg:text-[13px] font-semibold text-[var(--jc-text)] truncate">{c.keep.martha_to}</div>
            <div className="text-[13px] lg:text-[11.5px] text-[var(--jc-text-soft)]">{c.keep.martha_phone}</div>
          </div>
          <StatusPill status="sent" kind="estimate" t={(k) => (k === "status.sent" ? c.keep.sms_pill : k)} />
        </div>
        <div className="mt-3 rounded-lg bg-[#EEF3FE] px-3 py-2 text-[14px] lg:text-[12.5px] text-[var(--jc-text)] leading-snug">{c.keep.sms_body}</div>
        <div className="mt-2 flex items-center justify-between text-[12px] lg:text-[11px] text-[var(--jc-text-soft)]">
          <span className="flex items-center gap-1">
            <CheckCircle2 size={12} className="text-[var(--jc-green)]" />
            {c.keep.sms_sent}
          </span>
          <span>{c.keep.sms_time}</span>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-white shadow-lg p-3.5" data-testid="keep-calendar">
        <div className="flex items-center justify-between mb-2.5">
          <div className="min-w-0">
            <div className="text-[14.5px] lg:text-[13px] font-semibold text-[var(--jc-text)] truncate">{c.keep.cal_title}</div>
            <div className="text-[12.5px] lg:text-[11px] text-[var(--jc-text-soft)] truncate">{c.keep.cal_range}</div>
          </div>
          <div className="flex text-[11px] lg:text-[10.5px] font-semibold rounded-md overflow-hidden border border-[var(--jc-border)] flex-shrink-0">
            <span className="px-2 py-1 bg-[var(--jc-blue)] text-white">{c.keep.cal_week}</span>
            <span className="px-2 py-1 bg-white text-[var(--jc-text-soft)]">{c.keep.cal_month}</span>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-[11px] lg:text-[9.5px] text-center text-[var(--jc-text-soft)] mb-1">
          {c.keep.cal_days.map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {[0,1,2,3,4,5,6].map((i) => (
            <div key={i} className="h-16 lg:h-14 rounded-md bg-[#F5F7FB] border border-white p-0.5 flex flex-col gap-0.5">
              {evtColors[i] && evtLabels[i] && (
                <span className={`text-[10px] lg:text-[8.5px] font-semibold rounded px-1 py-0.5 truncate ${evtColors[i]}`}>{evtLabels[i]}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <VoiceBubble testid="keep-manuel-voice">{c.keep.manuel_voice}</VoiceBubble>
      <div className="rounded-xl bg-white border border-white shadow-lg p-4" data-testid="keep-manuel-confirm">
        <div className="flex items-center gap-2.5">
          <InitialsAvatar initials="MG" size={34} />
          <div className="flex-1 min-w-0">
            <div className="text-[14.5px] lg:text-[13px] font-semibold text-[var(--jc-text)]">{c.keep.manuel_to}</div>
            <div className="text-[13px] lg:text-[11.5px] text-[var(--jc-text-soft)]">{c.keep.manuel_role}</div>
          </div>
          <StatusPill status="sent" kind="estimate" t={(k) => (k === "status.sent" ? c.keep.manuel_email_pill : k)} />
        </div>
        <div className="mt-3 text-[14px] lg:text-[12.5px] leading-snug text-[var(--jc-text)]">
          {c.keep.manuel_body_es_verbatim}
        </div>
        <button type="button" className="mt-3 w-full jc-btn-blue text-white text-[14.5px] lg:text-[13px] font-semibold py-2.5 lg:py-2 rounded-md flex items-center justify-center gap-1">
          {c.keep.manuel_confirm}
        </button>
      </div>
    </div>
  );

  return (
    <section
      data-testid="features-keep"
      className="relative lg:min-h-[1360px] isolate overflow-hidden"
      style={{ background: "#E7EEF9" }}
    >
      {/* Desktop-only environmental canvas at z-0.
          Preserves the full canvas (no horizontal crop) pinned to
          the TOP of the section. The bottom 220px of the section
          is the section's pale-blue background, giving the benefit
          strip a clean lane BELOW the Today's Plan notebook so the
          notebook remains fully readable. */}
      <div
        className="hidden lg:block absolute inset-x-0 top-0 h-[960px] pointer-events-none"
        style={{ zIndex: 0 }}
        aria-hidden
      >
        <img
          src="/brand/features/keep_env_canvas.png"
          alt="Levi at his desk with the Today's Plan notebook, JobClose AI cap and Levi collar tag, PLAN. PREPARE. EXECUTE. REPEAT. wall sign, plant, mug and bookshelf behind him."
          draggable={false}
          data-testid="keep-env-canvas"
          className="w-full h-full object-cover object-center select-none"
        />
      </div>

      {/* Content — z-2 above canvas. Full-width outer wrapper. Right
          padding trimmed so the workflow stack can hug the right
          edge into the wall-sign / bookshelf strip. */}
      <div className="relative w-full px-6 lg:pl-[5%] lg:pr-6 pt-16 pb-12 lg:pt-20 lg:pb-16" style={{ zIndex: 2 }}>
        <div className="grid grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* LEFT — pale-blue panel: headline + body + 5 capability cards */}
          <div className="col-span-12 lg:col-span-6" data-testid="keep-left-zone">
            <h2 className="font-display tracking-tight text-[#0A1428] text-4xl sm:text-5xl lg:text-[58px] leading-[1.02]" data-testid="keep-headline">
              {c.keep.h2a}<br /><span className="text-[var(--jc-blue)]">{c.keep.h2b}</span>
            </h2>
            <p className="mt-4 text-[16.5px] lg:text-[17px] leading-[1.55] text-[var(--jc-text-soft)] max-w-lg" data-testid="keep-body">
              {c.keep.lede}
            </p>

            <div className="mt-8 space-y-4 max-w-[520px]" data-testid="keep-cards">
              {capabilities.map((cc) => (
                <div
                  key={cc.testid}
                  data-testid={cc.testid}
                  className="rounded-2xl bg-white/95 border border-white shadow-md p-5 lg:p-5 flex items-center gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[var(--jc-blue)] grid place-items-center text-white">
                    <cc.icon size={24} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[17.5px] lg:text-[16.5px] font-bold leading-tight text-[var(--jc-text)]">{cc.title}</div>
                    <div className="text-[15px] lg:text-[13.5px] text-[var(--jc-text-soft)] leading-snug mt-1.5 lg:mt-1">{cc.body}</div>
                  </div>
                  <div className="flex-shrink-0">{cc.right}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — hugs right edge; sits over wall-sign/bookshelf
              area, above and to the right of Levi. Cards are narrow
              and stacked so they never overlap Levi's body or the
              Today's Plan notebook. */}
          <div className="hidden lg:block lg:col-span-6" data-testid="keep-right-zone">
            <div className="ml-auto w-[275px]">
              <RightUI />
            </div>
          </div>
        </div>

        {/* Bottom benefit strip — DESKTOP ONLY. Positioned in the
            clean pale-blue zone BELOW the environmental image
            (image occupies top 960px; strip sits in the bottom
            zone) so it never overlaps the Today's Plan notebook.
            Enlarged to feel like an intentional major closing
            element for Section 4. */}
        <div className="hidden lg:block max-w-none mx-auto px-[2%] mt-12 lg:mt-[160px]">
          <div
            className="rounded-2xl bg-white/95 border border-white shadow-xl px-10 lg:px-12 py-10 lg:py-12"
            data-testid="keep-value-strip"
          >
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-14">
              {value.map((v) => (
                <div key={v.testid} data-testid={v.testid} className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-[#EEF3FE] grid place-items-center text-[var(--jc-blue)]">
                    <v.icon size={32} strokeWidth={2.2} />
                  </div>
                  <div>
                    <div className="text-[19px] font-bold text-[var(--jc-text)] leading-tight">{v.t}</div>
                    <div className="text-[15.5px] text-[var(--jc-text-soft)] leading-snug mt-2">{v.b}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE ONLY · dedicated vertical stack — Levi visual +
          right-side UI cards + stacked benefits. Desktop layout
          above is untouched. */}
      <div className="lg:hidden max-w-7xl mx-auto px-6 pb-8 space-y-6" data-testid="keep-mobile-env">
        <div className="w-full h-[440px] rounded-2xl overflow-hidden shadow-xl bg-[#0B1E3E]" data-testid="keep-mobile-levi">
          <img
            src="/brand/features/keep_env_canvas.png"
            alt="Levi at his desk with the Today's Plan notebook, JobClose AI cap and Levi collar tag, wall sign and office environment."
            draggable={false}
            className="w-full h-full object-cover select-none"
            style={{ objectPosition: "68% 55%", transform: "scale(1.28)", transformOrigin: "68% 55%" }}
          />
        </div>
        <div data-testid="keep-mobile-right-stack">
          <RightUI compact />
        </div>
        <div className="rounded-2xl bg-white border border-[var(--jc-border)] shadow-lg p-6 space-y-6" data-testid="keep-value-strip-mobile">
          {value.map((v) => (
            <div key={`m-${v.testid}`} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#EEF3FE] grid place-items-center text-[var(--jc-blue)]">
                <v.icon size={24} strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[17px] font-bold text-[var(--jc-text)] leading-tight">{v.t}</div>
                <div className="text-[15.5px] text-[var(--jc-text-soft)] leading-snug mt-1.5">{v.b}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
